<template>
  <!-- Check login strategy by comercial -->
  <v-container class="mt-4">
    <v-card outlined class="mx-auto mb-4" max-width="1520">
      <v-progress-linear
        v-if="loading"
        indeterminate
        color="primary"
      ></v-progress-linear>
      <v-row
        no-gutters
        class="pa-5"
        style="display: flex; flex-direction: column"
      >
        <h2 style="font-size: 1.5rem" class="font-weight-regular mb-8">
          Redirecionamento de login para agendamento de demonstração
        </h2>
        <p>
          Ao ativar esta configuração, novos acessos aos aplicativos Conecta
          Suite e Conecta Control serão redirecionados automaticamente para uma
          landing page de agendamento de demonstração.
        </p>
        <Alert dense class="my-1">
          <v-row align="center" no-gutters>
            <v-col class="grow blue--text text--lighten-1"
              >Importante: clientes que já acessaram anteriormente os
              aplicativos não serão impactados.</v-col
            >
          </v-row>
        </Alert>

        <div class="d-flex align-center mb-5">
          <v-switch
            class="mx-3"
            color="accent"
            v-model="configStatus"
            inset
            @change="changeLoginConfig()"
            :disabled="loading"
          ></v-switch>
          <div>
            <p class="ma-0 font-weight-medium">
              {{
                `Redirecionamento de login ${
                  configStatus ? "ATIVADO" : "DESATIVADO"
                } `
              }}
            </p>
          </div>
        </div>

        <div class="trial-links">
          <h4>Links para acesso direto ao Trial</h4>
          <p>
            Esses links devem ser enviados pelo time Conecta ou Parceiros para o
            cliente:
          </p>

          <div class="link-item">
            <div style="display: flex; align-items: center">
              <label for="link-suite"><strong>Conecta Suite:</strong></label>
              <v-text-field
                class="mx-4"
                hide-details
                inputmode
                outlined
                dense
                id="link-suite"
                value="https://app.conectasuite.com/login?action=allow_trial"
                readonly
                style="flex: 1; padding: 6px; max-width: 550px"
              />
              <v-btn color="primary" @click="copyToClipboard('link-suite')"
                ><v-icon v-text="'ph-copy-simple'" class="mr-3" />Copiar</v-btn
              >
            </div>
          </div>

          <div class="link-item mb-5" style="margin-top: 12px">
            <div style="display: flex; align-items: center">
              <label for="link-control"
                ><strong>Conecta Control:</strong></label
              >
              <v-text-field
                class="mr-4"
                hide-details
                inputmode
                outlined
                dense
                id="link-control"
                value="https://app.conectacontrol.com/login?action=allow_trial"
                readonly
                style="flex: 1; padding: 6px; max-width: 550px"
              />

              <v-btn color="primary" @click="copyToClipboard('link-control')"
                ><v-icon v-text="'ph-copy-simple'" class="mr-3" />Copiar</v-btn
              >
            </div>
          </div>
        </div>
        <div class="redirect-config-info">
          <h4>Regras de funcionamento</h4>
          <ul>
            <li>
              Para fazer o <strong>primeiro acesso ao Trial</strong> é
              necessário possuir o link direto.
            </li>
            <li>
              O redirecionamento para o agendamento de demonstração ocorre em
              todos os acessos que não forem pelo link direto.
            </li>
            <li>
              Clientes com acessos anteriores ao Suite ou Control
              <strong>seguem com login normal</strong>.
            </li>
          </ul>

          <h4 class="mt-5">Dicas para uso comercial</h4>
          <ul>
            <li>
              <strong>Recomendado:</strong> envie os dois links e oriente o
              cliente a instalar ambos os aplicativos.
            </li>
          </ul>

          <h4 class="mt-5">Clientes Microsoft</h4>
          <p>
            Mesmo sendo redirecionado para o agendamento da demonstração, a
            instalação do aplicativo <strong>permanece obrigatória</strong>. No
            primeiro acesso, o sistema solicitará isso normalmente.
          </p>

          <h4>Acesso de Parceiros</h4>
          <p>
            Parceiros devem enviar os mesmos links para seus clientes acessarem
            o Trial. Caso contrário, o redirecionamento para o agendamento de
            demonstração será aplicado também a eles.
          </p>
        </div>
      </v-row>
    </v-card>
  </v-container>
</template>
<script>
import { getDatabase, ref, set, get } from "firebase/database";
import { mapGetters, mapMutations } from "vuex";
import { getAnalytics, logEvent } from "firebase/analytics";
import moment from "moment";
export default {
  name: "DemoLoginConfig",

  data() {
    return {
      configStatus: false,
      loading: false,
    };
  },

  computed: {
    ...mapGetters(["currentUser", "company"]),
  },

  methods: {
    ...mapMutations(["setSnackBar"]),
    resetAddDialog() {
      this.showDialogAddDomain = false;
    },

    copyToClipboard(elementId) {
      const input = document.getElementById(elementId);
      input.select();
      input.setSelectionRange(0, 99999); // For mobile devices
      document.execCommand("copy");

      this.setSnackBar({
        message: "Link copiado para transferência",
        color: "success",
        show: true,
      });
    },

    setActionLog() {
      const analytics = getAnalytics();
      const { main_domain } = this.company;
      const clicked_where =
        "Switch de ativação/desativação do redirecionamento para a agenda de demonstração";

      logEvent(analytics, "change_demo_redirect", {
        main_domain,
        clicked_where,
        date: moment().format("DD/MM/YYYY"),
        currentUser: this.currentUser.email,
        action: `${
          this.configStatus ? "Ativou" : "Desativou"
        } o redirecionamento para o agendamento de demonstração`,
      });
    },

    getConfig() {
      this.loading = true;
      const db = getDatabase();
      const reference = ref(db, `demo_login_config`);

      get(reference)
        .then((snapshot) => {
          if (snapshot.exists()) {
            const { is_active, allowed_domains } = snapshot.val();
            this.configStatus = is_active;
            this.allowedDomains = allowed_domains || [];
          }
          this.loading = false;
        })
        .catch((error) => {
          console.error(error);
          this.loading = false;
        });
    },

    changeLoginConfig() {
      this.loading = true;

      const db = getDatabase();
      const reference = ref(db, "demo_login_config/is_active");

      set(reference, this.configStatus);

      this.setActionLog();
      this.loading = false;
    },
  },
  async beforeMount() {
    await this.getConfig();
  },
};
</script>
<style>
.v-expansion-panel-content__wrap {
  padding: 0px !important;
}
</style>
